import { Card, Tabs } from "@rothoblaas/design-system";
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FC,
  type RefObject,
} from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import useTabs from "../../../hooks/useTabs";
import useI18n from "../../../hooks/useTranslations";
import { computeDeckDesignerResult, type DeckDesignerResult } from "../../../http/deckDesigner/formulas";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";
import BillOfQuantitiesCard from "./BillOfQuantitiesCard";
import BoardFitWarning, { type AppliedFitInfo } from "./BoardFitWarning";
import CalculationDetailsCard from "./CalculationDetailsCard";
import DeckBottomViewCard from "./DeckBottomViewCard";
import styles from "./DeckDesigner.module.css";
import {
  DECK_DESIGNER_PREVIEW_TAB_PARAMETER,
  DECK_DESIGNER_RESULTS_TAB_PARAMETER,
  PREVIEW_TAB_INDEX,
  RESULTS_CARD_ID,
  RESULTS_TAB_INDEX,
  type GoToTabs,
} from "./deckDesignerTabs";
import DeckDrawingCard from "./DeckDrawingCard";
import DeckSlope3DView from "./DeckSlope3DView";
import DeckSlopeViewCard from "./DeckSlopeViewCard";
import FinishedDeckSectionCard from "./FinishedDeckSectionCard";
import {
  capturePreviewImage,
  getCaptureFontEmbedCSS,
  waitForStableContent,
  type CapturedPreviewImage,
  type FinalReportImageKind,
} from "./previewCapture";
import { useBillOfQuantities } from "./useBillOfQuantities";

type DeckDesignerResultsProps = {
  calculationId: string | undefined;
  configurationName: string | undefined;
  projectName: string | undefined;
  accentColor: string;
  hasFormErrors: boolean;
  // Clearing the drawing (DeckDrawingCard's trash button) drops ShapeAndSizeCard's cache too.
  onDimensionsCleared: () => void;
  goToTabs: GoToTabs;
};

const buildDifferentBoardWidthDependencySignature = (
  values: Pick<
    DeckDesignerConfigurationFormValues,
    "shape" | "base" | "height" | "secondaryBase" | "secondaryHeight" | "hasHorizontalBoards" | "gap" | "boardWidth"
  >,
): string => {
  // Only the axis boards actually span across affects whether an applied different-board-width
  // fit still holds - same isHorizontal selection computeBoardFit/computeBoardQuantity use in
  // formulas.ts. Including both axes unconditionally reset the fit (and re-showed the warning)
  // whenever the OTHER, board-length axis changed, even though it can't affect the fit at all.
  const isHorizontal = values.hasHorizontalBoards !== false;

  return JSON.stringify({
    shape: values.shape,
    countLength: isHorizontal ? values.height : values.base,
    cutoutLength: isHorizontal ? values.secondaryHeight : values.secondaryBase,
    hasHorizontalBoards: values.hasHorizontalBoards,
    gap: values.gap,
    boardWidth: values.boardWidth,
  });
};

// The results Card (preview, bill of quantities, calculation details) and the off-screen copies the PDF
// is captured from. Owns the result: it re-renders on the fields the result is computed from, the form
// doesn't. Memoized, with stable props from DeckDesigner.
const DeckDesignerResults: FC<DeckDesignerResultsProps> = ({
  calculationId,
  configurationName,
  projectName,
  accentColor,
  hasFormErrors,
  onDimensionsCleared,
  goToTabs,
}) => {
  const i18n = useI18n();
  const { control, setValue } = useFormContext<DeckDesignerConfigurationFormValues>();
  const previewTabs = useTabs(Object.keys(PREVIEW_TAB_INDEX).length, DECK_DESIGNER_PREVIEW_TAB_PARAMETER);
  const resultsTabs = useTabs(Object.keys(RESULTS_TAB_INDEX).length, DECK_DESIGNER_RESULTS_TAB_PARAMETER);
  const differentBoardWidthDependencySignatureRef = useRef<string | null>(null);
  const drawingContainerRef = useRef<HTMLDivElement>(null);
  const bottomViewContainerRef = useRef<HTMLDivElement>(null);
  const bottomViewPlainContainerRef = useRef<HTMLDivElement>(null);
  const sectionContainerRef = useRef<HTMLDivElement>(null);
  const slopeViewContainerRef = useRef<HTMLDivElement>(null);
  const captureStageRef = useRef<HTMLDivElement>(null);

  const shape = useWatch({ control, name: "shape", exact: true });
  const base = useWatch({ control, name: "base", exact: true });
  const height = useWatch({ control, name: "height", exact: true });
  const liveLoad = useWatch({ control, name: "liveLoad", exact: true });
  const deflection = useWatch({ control, name: "deflection", exact: true });
  const secondaryBase = useWatch({ control, name: "secondaryBase", exact: true });
  const secondaryHeight = useWatch({ control, name: "secondaryHeight", exact: true });
  const totalHeight = useWatch({ control, name: "totalHeight", exact: true });
  const hasHorizontalBoards = useWatch({ control, name: "hasHorizontalBoards", exact: true });
  const boardWidth = useWatch({ control, name: "boardWidth", exact: true });
  const longDifferentBoardWidth = useWatch({ control, name: "longDifferentBoardWidth", exact: true });
  const shortDifferentBoardWidth = useWatch({ control, name: "shortDifferentBoardWidth", exact: true });
  const boardThickness = useWatch({ control, name: "boardThickness", exact: true });
  const gap = useWatch({ control, name: "gap", exact: true });
  const fasteningType = useWatch({ control, name: "fasteningType", exact: true });
  const connector = useWatch({ control, name: "connector", exact: true });
  const connectorSubstructureScrew = useWatch({ control, name: "connectorSubstructureScrew", exact: true });
  const connectorBoardScrew = useWatch({ control, name: "connectorBoardScrew", exact: true });
  const substructureMaterialClass = useWatch({ control, name: "substructureMaterialClass", exact: true });
  const substructure = useWatch({ control, name: "substructure", exact: true });
  const joistsSpacing = useWatch({ control, name: "joistsSpacing", exact: true });
  const secondaryItem = useWatch({ control, name: "secondaryItem", exact: true });
  const support = useWatch({ control, name: "support", exact: true });
  const supportScrew = useWatch({ control, name: "supportScrew", exact: true });

  // The last committed result, whose unchanged stages the next one keeps (see computeDeckDesignerResult).
  const previousResultRef = useRef<DeckDesignerResult | null>(null);
  const deckDesignerResult = useMemo(
    () =>
      computeDeckDesignerResult(
        {
          liveLoad,
          deflection,
          shape,
          base,
          height,
          secondaryBase,
          secondaryHeight,
          totalHeight,
          hasHorizontalBoards,
          fasteningType,
          gap,
          connector,
          connectorSubstructureScrew,
          connectorBoardScrew,
          boardWidth,
          longDifferentBoardWidth,
          shortDifferentBoardWidth,
          boardThickness,
          substructureMaterialClass,
          substructure,
          joistsSpacing,
          secondaryItem,
          support,
          supportScrew,
        },
        previousResultRef.current,
      ),
    [
      liveLoad,
      deflection,
      shape,
      base,
      height,
      secondaryBase,
      secondaryHeight,
      totalHeight,
      hasHorizontalBoards,
      fasteningType,
      gap,
      connector,
      connectorSubstructureScrew,
      connectorBoardScrew,
      boardWidth,
      longDifferentBoardWidth,
      shortDifferentBoardWidth,
      boardThickness,
      substructureMaterialClass,
      substructure,
      joistsSpacing,
      secondaryItem,
      support,
      supportScrew,
    ],
  );

  useEffect(() => {
    previousResultRef.current = deckDesignerResult;
  }, [deckDesignerResult]);

  const boardQuantity = deckDesignerResult.boardQuantity;
  const boardFit = deckDesignerResult.boardFit;
  const hasBoardFitIssue = boardFit?.hasIssue ?? false;
  // Shared by both BoardFitWarning instances (preview and bill of quantities tabs), so the "applied"
  // box and its undo survive the switch to the preview after a fix. Dropped when a new issue appears.
  const [appliedBoardFit, setAppliedBoardFit] = useState<AppliedFitInfo | null>(null);
  const previousHasBoardFitIssueRef = useRef(hasBoardFitIssue);

  useEffect(() => {
    if (hasBoardFitIssue && !previousHasBoardFitIssueRef.current) {
      setAppliedBoardFit(null);
    }
    previousHasBoardFitIssueRef.current = hasBoardFitIssue;
  }, [hasBoardFitIssue]);

  // After a board fit fix, show its result in the top view. One navigation for both tabs.
  // The board fit handlers are useCallback'd: BoardFitWarning is memoized.
  const showBoardFitInTopView = useCallback(
    () =>
      goToTabs({
        [DECK_DESIGNER_RESULTS_TAB_PARAMETER]: RESULTS_TAB_INDEX.preview,
        [DECK_DESIGNER_PREVIEW_TAB_PARAMETER]: PREVIEW_TAB_INDEX.plane,
      }),
    [goToTabs],
  );
  // Through useFormState, not formState from the context: reading the latter subscribes useForm
  // itself, whose re-render hands FormProvider a new value and so re-renders every card despite
  // their memo.
  const { isDirty: isFormDirty } = useFormState({ control });
  const differentBoardWidthDependencySignature = useMemo(
    () =>
      buildDifferentBoardWidthDependencySignature({
        shape,
        base,
        height,
        secondaryBase,
        secondaryHeight,
        hasHorizontalBoards,
        gap,
        boardWidth,
      }),
    [base, boardWidth, gap, hasHorizontalBoards, height, shape, secondaryBase, secondaryHeight],
  );

  useEffect(() => {
    const previousSignature = differentBoardWidthDependencySignatureRef.current;
    differentBoardWidthDependencySignatureRef.current = differentBoardWidthDependencySignature;

    if (!isFormDirty || previousSignature == null || previousSignature === differentBoardWidthDependencySignature) {
      return;
    }

    if (longDifferentBoardWidth != null) {
      setValue("longDifferentBoardWidth", null, {
        shouldDirty: true,
      });
    }

    if (shortDifferentBoardWidth != null) {
      setValue("shortDifferentBoardWidth", null, {
        shouldDirty: true,
      });
    }
  }, [
    differentBoardWidthDependencySignature,
    isFormDirty,
    longDifferentBoardWidth,
    setValue,
    shortDifferentBoardWidth,
  ]);

  const applyBoardFit = useCallback(() => {
    if (!boardFit?.suggestedBoardWidth) {
      return;
    }

    setValue("boardWidth", boardFit.suggestedBoardWidth, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [boardFit?.suggestedBoardWidth, setValue]);

  const applyDifferentBoardWidthFit = useCallback(() => {
    if (!boardFit?.suggestedLongDifferentBoardWidth && !boardFit?.suggestedShortDifferentBoardWidth) {
      return;
    }

    setValue("longDifferentBoardWidth", boardFit.suggestedLongDifferentBoardWidth ?? null, {
      shouldDirty: true,
    });
    setValue("shortDifferentBoardWidth", boardFit.suggestedShortDifferentBoardWidth ?? null, {
      shouldDirty: true,
    });
  }, [boardFit?.suggestedLongDifferentBoardWidth, boardFit?.suggestedShortDifferentBoardWidth, setValue]);

  // The off-screen copies below (see .captureStage) are mounted only while the PDF is being
  // generated, and only those of the images it asks for: always mounted, they re-rendered (bottom-view
  // geometry included) on every form change. Resolved by the effect below once React has committed
  // them, so their refs are set.
  const [captureStageKinds, setCaptureStageKinds] = useState<ReadonlySet<FinalReportImageKind> | null>(null);
  const captureStageMountedResolverRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (captureStageKinds) {
      captureStageMountedResolverRef.current?.();
      captureStageMountedResolverRef.current = null;
    }
  }, [captureStageKinds]);

  // Captures from the off-screen copies rather than the visible preview - switching the visible pane
  // through each view and re-fitting it to export framing would flash in front of the user, who never
  // asked to see that.
  const captureFinalReportImages = useCallback(
    async (kinds: FinalReportImageKind[]): Promise<CapturedPreviewImage[]> => {
      await new Promise<void>((resolve) => {
        captureStageMountedResolverRef.current = resolve;
        setCaptureStageKinds(new Set(kinds));
      });

      try {
        const captureTargets: { containerRef: RefObject<HTMLDivElement | null>; kind: FinalReportImageKind }[] = [
          { containerRef: bottomViewContainerRef, kind: "bottom_view" },
          { containerRef: bottomViewPlainContainerRef, kind: "bottom_view_plain" },
          { containerRef: drawingContainerRef, kind: "top_view" },
          { containerRef: sectionContainerRef, kind: "side_view" },
          { containerRef: slopeViewContainerRef, kind: "slope_view" },
        ];
        const targets = captureTargets.flatMap(({ containerRef, kind }) =>
          containerRef.current ? [{ element: containerRef.current, kind }] : [],
        );

        await waitForStableContent(targets.map(({ element }) => element));
        const fontEmbedCSS = captureStageRef.current
          ? await getCaptureFontEmbedCSS(captureStageRef.current)
          : undefined;

        const captures: CapturedPreviewImage[] = [];
        for (const { element, kind } of targets) {
          captures.push(await capturePreviewImage(kind, element, fontEmbedCSS));
        }

        return captures;
      } finally {
        setCaptureStageKinds(null);
      }
    },
    [],
  );

  const { rows, hasAnyResult, exportActions, pdfExport } = useBillOfQuantities(
    calculationId,
    deckDesignerResult,
    configurationName,
    projectName,
    captureFinalReportImages,
    control,
    hasFormErrors,
  );

  return (
    <>
      {/* Preview, bill of quantities and calculation details are tabs of one Card; the preview's
          views are sub-tabs. Same URL-only switching as the preview tabs (see below), and only
          the selected tab is mounted: the preview views size themselves on mount. */}
      <Card id={RESULTS_CARD_ID} className={styles.resultsCard} accentColor="var(--accent-base, var(--red))" shadow>
        <Tabs
          controller={resultsTabs}
          renderMode="eager"
          tabs={[
            {
              title: i18n.translation.common.preview,
              onClick: () => resultsTabs.goToTab(RESULTS_TAB_INDEX.preview),
              hasSubTabs: true,
            },
            {
              title: i18n.translation.deckDesignerModule.configurations.calculation.billOfQuantities,
              onClick: () => resultsTabs.goToTab(RESULTS_TAB_INDEX.billOfQuantities),
            },
            {
              title: i18n.translation.deckDesignerModule.configurations.calculation.calculationDetails,
              onClick: () => resultsTabs.goToTab(RESULTS_TAB_INDEX.calculationDetails),
            },
          ]}
          contents={[
            resultsTabs.selectedIndex === RESULTS_TAB_INDEX.preview ? (
              <div className={styles.previewSection}>
                {/* The design-system Tabs keeps its own selected index (synced from the URL) and ignores
                    the controller's, so clicks go through the URL only (tab.onClick): that way it and
                    previewTabs switch in the same render, instead of Tabs showing the new panel one
                    render before its content arrives - an empty flash that made the footer below
                    jump up. "eager" skips the lazy mode's extra render before a first-visited tab
                    mounts; only the selected view is ever non-null anyway. */}
                <Tabs.SubTab>
                  <Tabs
                    controller={previewTabs}
                    renderMode="eager"
                    tabs={[
                      {
                        title: i18n.translation.deckDesignerModule.configurations.calculation.topView,
                        onClick: () => previewTabs.goToTab(PREVIEW_TAB_INDEX.plane),
                      },
                      {
                        title: i18n.translation.deckDesignerModule.configurations.calculation.sideView,
                        onClick: () => previewTabs.goToTab(PREVIEW_TAB_INDEX.section),
                      },
                      {
                        title: i18n.translation.deckDesignerModule.configurations.calculation.bottomView,
                        onClick: () => previewTabs.goToTab(PREVIEW_TAB_INDEX.bottom),
                      },
                      {
                        title: i18n.translation.deckDesignerModule.configurations.calculation.slopeView,
                        onClick: () => previewTabs.goToTab(PREVIEW_TAB_INDEX.slope),
                      },
                    ]}
                    contents={[
                      previewTabs.selectedIndex === PREVIEW_TAB_INDEX.plane ? (
                        <div className={styles.previewPane}>
                          <DeckDrawingCard
                            embedded
                            boardQuantity={boardQuantity}
                            onDimensionsCleared={onDimensionsCleared}
                          />
                        </div>
                      ) : null,
                      previewTabs.selectedIndex === PREVIEW_TAB_INDEX.section ? (
                        <div className={styles.previewPane}>
                          <FinishedDeckSectionCard embedded />
                        </div>
                      ) : null,
                      previewTabs.selectedIndex === PREVIEW_TAB_INDEX.bottom ? (
                        <div className={styles.previewPane}>
                          <DeckBottomViewCard embedded result={deckDesignerResult} />
                        </div>
                      ) : null,
                      previewTabs.selectedIndex === PREVIEW_TAB_INDEX.slope ? (
                        <div className={styles.previewPane}>
                          <DeckSlope3DView />
                        </div>
                      ) : null,
                    ]}
                  />
                </Tabs.SubTab>
                {/* Outside Tabs.SubTab, so the "ul ~ div" rules only hit the Tabs panels. */}
                <section className={styles.previewFooter}>
                  <BoardFitWarning
                    boardFit={boardFit}
                    boardQuantity={boardQuantity}
                    onApplyBoardFit={applyBoardFit}
                    onApplyDifferentBoardWidthFit={applyDifferentBoardWidthFit}
                    appliedFit={appliedBoardFit}
                    onAppliedFitChange={setAppliedBoardFit}
                    onFitApplied={showBoardFitInTopView}
                  />
                </section>
              </div>
            ) : null,
            resultsTabs.selectedIndex === RESULTS_TAB_INDEX.billOfQuantities ? (
              <div className={styles.resultsSection}>
                <BillOfQuantitiesCard rows={rows} exportActions={exportActions} pdfExport={pdfExport} embedded />
                {/* Repeated here, under the PDF button: an unresolved board fit is what keeps it disabled. */}
                <BoardFitWarning
                  boardFit={boardFit}
                  boardQuantity={boardQuantity}
                  onApplyBoardFit={applyBoardFit}
                  onApplyDifferentBoardWidthFit={applyDifferentBoardWidthFit}
                  appliedFit={appliedBoardFit}
                  onAppliedFitChange={setAppliedBoardFit}
                  onFitApplied={showBoardFitInTopView}
                />
              </div>
            ) : null,
            resultsTabs.selectedIndex === RESULTS_TAB_INDEX.calculationDetails ? (
              <div className={styles.resultsSection}>
                <CalculationDetailsCard result={deckDesignerResult} hasAnyResult={hasAnyResult} embedded />
              </div>
            ) : null,
          ]}
        />
      </Card>

      {/* Off-screen copies dedicated to PDF capture, rendered in export framing so the visible
          preview above never has to switch view/zoom in front of the user. Mounted only while the
          PDF is being generated (see captureFinalReportImages). A child of the workspace grid, but
          position: fixed takes it out of the grid's flow, and the workspace sets no inherited style
          but --accent-base, set here too: it lays out and renders as it did next to FlexContent. */}
      {captureStageKinds && (
        <div
          ref={captureStageRef}
          className={styles.captureStage}
          style={{ "--accent-base": accentColor } as CSSProperties}
          aria-hidden
        >
          {captureStageKinds.has("top_view") && (
            <DeckDrawingCard embedded boardQuantity={boardQuantity} planeContentRef={drawingContainerRef} exportMode />
          )}
          {captureStageKinds.has("bottom_view") && (
            <DeckBottomViewCard
              embedded
              result={deckDesignerResult}
              planeContentRef={bottomViewContainerRef}
              exportMode
            />
          )}
          {captureStageKinds.has("bottom_view_plain") && (
            <DeckBottomViewCard
              embedded
              result={deckDesignerResult}
              planeContentRef={bottomViewPlainContainerRef}
              exportMode
              hideMeasurements
              highlightSupportNodeReferences
            />
          )}
          {captureStageKinds.has("side_view") && (
            <div ref={sectionContainerRef}>
              <FinishedDeckSectionCard embedded />
            </div>
          )}
          {captureStageKinds.has("slope_view") && (
            <div ref={slopeViewContainerRef}>
              <DeckSlopeViewCard embedded />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default memo(DeckDesignerResults);
